import InfoIcon from "@mui/icons-material/Info";
import { Box, Chip, Typography } from "@mui/material";
import pluralize from "pluralize";
import useColors from "../../../../../../../api/hooks/useColors";
import CustomTooltip from "../../../../../../../components/CustomTooltip";
import ExternalLink from "../../../../../../../components/ExternalLink";
import SvgToIcon from "../../../../../../../icons/SvgToIcon";
import {
  AWS_S3_BUCKET_PUBLIC_READ_CONTROL_IDS,
  AZURE_LIST_BLOB_CONTAINER_CONTROL_IDS,
  ComplexEntityTypes,
  CONTROLS_WITH_OTHER_MDM_COLUMN,
  GoogleFileHeader,
  MICROSOFT_ACTIVE_DIRECTORY_GROUP_MEMBER_COUNT_CONTROL_IDS,
  PublicDataStorePermissions,
  SHARED_BOX_FILE_CONTROL_IDS,
  SHARED_GOOGLE_DRIVE_FILE_CONTROL_IDS,
  SHARED_PANORAYS_SUPPLIERS_CONTROL_IDS,
} from "../../../../../../../utils/constants";
import { getUserLastActiveTimestamp } from "../../../../../../../utils/search";
import { getPrettyDate } from "../../../../../../../utils/timestamp";
import { isComplexEntityType } from "../../../../../../../utils/utils";
import { getS3BucketPublicURL } from "../../../../../../stack/environment/components/views/dataStore/DataStoreDetailsView";
import { ReactComponent as ArrowRightIcon } from "../../../../../../stack/environment/icons/arrow_icons/arrow-right-currentColor-24.svg";
import { ReactComponent as EnvironmentIcon } from "../../../../../../stack/environment/icons/sidebar_icons/sidebar-icons-environment.svg";
import IntegrationImage from "../../../../../../stack/integrations/components/IntegrationImage";
import {
  IntegrationCategory,
  IntegrationsMapping,
} from "../../../../../../stack/integrations/IntegrationsMapping";
import JiraInteractionManager from "../../../../../../stack/integrations/Jira/JiraInteractionManager";
import { capitalizeAndRemoveUnderscores } from "../../../EntitiesSection/utils";
import {
  addToAssetColumn,
  getDisplayNameColumn,
  getExtraInfoColumn,
  getFirstDiscoveredColumn,
  getLabelColumn,
  getSimpleEntityDisplayNameColumn,
  getThirdInfoColumn,
} from "../../shared/sharedColumns";
import ExclusionInformationIcon from "./ExclusionInformationIcon";
import MisconfigurationMoreActionsButton from "./MisconfigurationMoreActionsButton";
import SnoozeInformationIcon from "./SnoozeInformationIcon";

const getAwsSecurityGroupConsoleHref = (headerlessStartIndex, row) => {
  const awsConsoleHref = row?.extra_info?.[headerlessStartIndex];
  return typeof awsConsoleHref === "string" ? awsConsoleHref : "";
};

const renderAwsSecurityGroupIdCell = (headerlessStartIndex, params, colors) => {
  const href = getAwsSecurityGroupConsoleHref(headerlessStartIndex, params.row);
  const linkText = params.value || "-";

  return href ? (
    <ExternalLink
      linkText={
        <Typography
          variant="caption"
          color={colors.text_primary}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
          }}
        >
          {linkText}
        </Typography>
      }
      href={href}
      tooltipText="View security group in AWS"
      linkColor={colors.text_primary}
    />
  ) : (
    <Typography
      variant="caption"
      color={colors.grey_700}
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
      }}
    >
      {linkText}
    </Typography>
  );
};

const getGoogleDriveFileId = (extraInfo) => {
  const entry = extraInfo?.find((info) =>
    info?.startsWith(GoogleFileHeader.DOCUMENT_ID)
  );

  if (!entry) return undefined;

  const firstDash = entry.indexOf("-");
  return firstDash === -1 ? undefined : entry.slice(firstDash + 1);
};

const AffectedEntitiesChip = ({ handleOnClick, quantity }) => {
  const colors = useColors();

  return (
    <Chip
      label={
        <Box display="flex" alignItems="center" gap={1}>
          {`${quantity} ${pluralize("entity", quantity)}`}
          <ArrowRightIcon width="16px" style={{ color: colors.text_primary }} />
        </Box>
      }
      onClick={handleOnClick}
      icon={
        <SvgToIcon
          IconLogo={EnvironmentIcon}
          width="16px"
          style={{
            color: colors.text_primary,
          }}
        />
      }
      style={{
        background: colors.entity_chip_enabled_fill,
        borderRadius: "8px",
        fontSize: "12px",
        color: colors.text_primary,
        boxShadow: colors.entity_chip_enabled_shadow,
        height: "24px",
        paddingLeft: "4px",
      }}
      sx={{
        "& .MuiTouchRipple-root": { display: "none !important" },
        "& .MuiChip-label": { paddingX: "4px" },
      }}
    />
  );
};

export default function getMisconfigurationsTableColumns(
  fullControl,
  entityType,
  handleMisconfigurationClick,
  onEvidenceClick,
  colors,
  terminatedEntities
) {
  const misconfigurationById = fullControl?.misconfigurations?.reduce(
    (acc, m) => {
      if (m?.misconfiguration_id) acc[m.misconfiguration_id] = m;
      return acc;
    },
    {}
  );

  const misconfigurationByEntityId = fullControl?.misconfigurations?.reduce(
    (acc, m) => {
      if (m?.entity_id) acc[m.entity_id] = m;
      return acc;
    },
    {}
  );
  const addColumnsForComplexEntities = (columns, terminatedEntities) => {
    columns.push(addToAssetColumn);
    columns.push(getDisplayNameColumn(colors, terminatedEntities));
    if (
      !(
        entityType === ComplexEntityTypes.USER &&
        fullControl.tool === IntegrationsMapping.salesforce.codeName
      )
    )
      columns.push(getExtraInfoColumn(entityType, colors));
    const thirdColumn = getThirdInfoColumn(entityType, colors);
    if (thirdColumn) {
      columns.push(thirdColumn);
    }
  };

  const addColumnsForSimpleEntities = (columns) => {
    if (fullControl.control_id === 56) {
      columns.push(getExtraInfoColumn(entityType, colors));
    }
    columns.push(getSimpleEntityDisplayNameColumn(colors));
  };

  const columns = [];
  if (isComplexEntityType(entityType))
    addColumnsForComplexEntities(columns, terminatedEntities);
  else addColumnsForSimpleEntities(columns);

  if (fullControl.misconfigurations_extra_info_headers?.length > 0) {
    for (const [
      index,
      info,
    ] of fullControl.misconfigurations_extra_info_headers.entries()) {
      const isSourcesColumn =
        info.toLowerCase() === "sources" || info.toLowerCase() === "found in";
      const isLastLoginColumn =
        info.toLowerCase() === "last login" ||
        info.toLowerCase() === "last seen";
      const isPublicLink = info.toLowerCase() === "public link";
      const isSecurityGroupId = info.toLowerCase() === "link to sg";

      columns.push({
        field: `extra_info_${index}`,
        headerName: capitalizeAndRemoveUnderscores(info),
        flex: 1,
        valueGetter: (params) => {
          if (isLastLoginColumn) {
            const lastActiveTimestamp = getUserLastActiveTimestamp(params.row);
            return lastActiveTimestamp;
          }
          return params.row.extra_info[index];
        },
        renderCell: (params) => (
          <Box
            sx={{
              overflow: "hidden",
            }}
          >
            {isSourcesColumn ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexWrap: "wrap",
                }}
              >
                {params.value?.split(",").map((toolName, index) => (
                  <IntegrationImage
                    key={index}
                    name={toolName.trim().toLowerCase().replace(/ /g, "_")}
                    width="20px"
                    newLogo={true}
                    showToolTip={true}
                  />
                ))}
              </Box>
            ) : isLastLoginColumn ? (
              <Typography
                variant="caption"
                color={colors.grey_700}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {params.value && params.value > 0
                  ? getPrettyDate(params.value)
                  : "N/A"}
              </Typography>
            ) : isPublicLink ? (
              params.value ? (
                <ExternalLink
                  href={params.value}
                  text=""
                  linkText={
                    <Typography
                      variant="caption"
                      color={colors.text_primary}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                      }}
                    >
                      View link
                    </Typography>
                  }
                  linkColor={colors.text_primary}
                  tooltipText="View file in Salesforce"
                />
              ) : (
                "-"
              )
            ) : isSecurityGroupId ? (
              renderAwsSecurityGroupIdCell(
                fullControl?.misconfigurations_extra_info_headers?.length,
                params,
                colors
              )
            ) : (
              <Typography
                variant="caption"
                color={colors.grey_700}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {params.value}
              </Typography>
            )}
          </Box>
        ),
      });
    }
    // Add Member Count column for group entities in access mask controls
    if (
      MICROSOFT_ACTIVE_DIRECTORY_GROUP_MEMBER_COUNT_CONTROL_IDS.includes(
        fullControl.control_id
      ) &&
      entityType === "group"
    ) {
      columns.push({
        field: "member_count",
        headerName: "Member Count",
        flex: 1,
        valueGetter: (params) => {
          // For group entities, the member count is at the last position in extra_info
          return params.row.extra_info?.at(-1) || "0";
        },
        renderCell: (params) => (
          <Typography
            variant="caption"
            color={colors.grey_700}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
            }}
          >
            {params.value}
          </Typography>
        ),
      });
    }
  }

  if (CONTROLS_WITH_OTHER_MDM_COLUMN.includes(fullControl.control_id)) {
    const DEVICE_MANAGEMENT_TOOLS = Object.entries(IntegrationsMapping)
      .filter(
        ([_, integration]) => integration.category === IntegrationCategory.MDM
      )
      .map(([codeName]) => codeName);
    const primaryTool = fullControl.tool.toLowerCase();

    columns.push({
      field: "other_mdms",
      headerName: "Managed By",
      flex: 1,
      valueGetter: (params) => {
        const sources = params.row.sources || [];
        const mdmTools = sources.filter(
          (source) =>
            DEVICE_MANAGEMENT_TOOLS.includes(source.toLowerCase()) &&
            source.toLowerCase() !== primaryTool
        );
        mdmTools.sort((a, b) => a.localeCompare(b));
        return mdmTools.join(", ");
      },
      renderCell: (params) => (
        <Box
          sx={{
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            {params.value &&
              params.value
                .split(", ")
                .map((toolName, index) => (
                  <IntegrationImage
                    key={index}
                    name={toolName.trim().toLowerCase().replace(/ /g, "_")}
                    width="20px"
                    newLogo={true}
                    showToolTip={true}
                  />
                ))}
          </Box>
        </Box>
      ),
    });
  }

  const hasAnyAffectedEntities = fullControl.misconfigurations.some(
    (misconfiguration) => misconfiguration.affected_entities?.length > 0
  );

  columns.push(
    getFirstDiscoveredColumn(colors, (params) => {
      const misconfiguration = misconfigurationByEntityId[params.row.entity_id];
      return misconfiguration?.first_seen?.timestamp_utc || 0;
    })
  );

  if (hasAnyAffectedEntities) {
    columns.push({
      field: "affected_entity_ids",
      headerName: "Affected Entities",
      alignHeader: "left",
      align: "left",
      minWidth: 150,
      sortable: true,
      resizable: true,
      disableReorder: true,
      valueGetter: (params) => {
        return params.row.affected_entity_ids.length;
      },
      renderCell: (params) => {
        const misconfiguration =
          misconfigurationById[params.row.misconfiguration_id];
        const totalAffectedCount =
          misconfiguration?.affected_entities?.length || 0;
        const excludedAffectedCount =
          misconfiguration?.affected_entities?.filter((ae) => ae.is_excluded)
            ?.length || 0;
        const allAffectedExcluded =
          totalAffectedCount > 0 &&
          excludedAffectedCount === totalAffectedCount;

        const tooltipTitle =
          params.value > 0
            ? `View affected ${pluralize("entity", params.value)}`
            : allAffectedExcluded
            ? `There ${
                excludedAffectedCount === 1 ? "is" : "are"
              } ${excludedAffectedCount} excluded affected ${pluralize(
                "entity",
                excludedAffectedCount
              )} to view`
            : "No affected entities";
        return (
          <CustomTooltip title={tooltipTitle} placement="top">
            <Box
              paddingRight="20px"
              display="flex"
              alignItems="center"
              gap={0.5}
            >
              {params.value > 0 ? (
                <AffectedEntitiesChip
                  handleOnClick={() => {
                    handleMisconfigurationClick(params.row.misconfiguration_id);
                  }}
                  quantity={params.value}
                />
              ) : allAffectedExcluded ? (
                <>
                  <AffectedEntitiesChip
                    handleOnClick={() => {
                      handleMisconfigurationClick(
                        params.row.misconfiguration_id
                      );
                    }}
                    quantity={0}
                  />
                  <InfoIcon
                    sx={{
                      width: "16px",
                      height: "16px",
                      color: colors.tooltip_info_icon,
                      cursor: "pointer",
                    }}
                  />
                </>
              ) : (
                "-"
              )}
            </Box>
          </CustomTooltip>
        );
      },
    });
  }

  if (SHARED_GOOGLE_DRIVE_FILE_CONTROL_IDS.includes(fullControl.control_id)) {
    columns.push({
      field: "link",
      headerName: "",
      align: "right",
      sortable: false,
      width: 70,
      maxWidth: 70,
      disableReorder: true,
      renderCell: (params) => {
        const fileId = getGoogleDriveFileId(params.row.extra_info);
        return (
          <ExternalLink
            href={`https://drive.google.com/open?id=${fileId}`}
            text=""
            linkText={""}
            linkColor={colors.text_primary}
            tooltipText="View file in Google Drive"
          />
        );
      },
    });
  }

  if (SHARED_BOX_FILE_CONTROL_IDS.includes(fullControl.control_id)) {
    columns.push({
      field: "link",
      headerName: "",
      align: "right",
      sortable: false,
      width: 70,
      maxWidth: 70,
      disableReorder: true,
      renderCell: (params) => {
        const sharedLinkUrl =
          params.row.extra_info[params.row.extra_info.length - 1];
        if (!sharedLinkUrl || sharedLinkUrl === "No link") {
          return null;
        }
        return (
          <ExternalLink
            href={sharedLinkUrl}
            text=""
            linkText={""}
            linkColor={colors.text_primary}
            tooltipText="View file in Box"
          />
        );
      },
    });
  }

  if (SHARED_PANORAYS_SUPPLIERS_CONTROL_IDS.includes(fullControl.control_id)) {
    columns.push({
      field: "link",
      headerName: "",
      align: "right",
      sortable: false,
      width: 70,
      maxWidth: 70,
      disableReorder: true,
      renderCell: (params) => {
        if (params.row.extra_info.length === 0) {
          return "-";
        }
        const supplierId =
          params.row.extra_info[params.row.extra_info.length - 1];
        return (
          <ExternalLink
            href={`https://panoraysapp.com/supplier/${supplierId}`}
            text=""
            linkText={""}
            linkColor={colors.text_primary}
            tooltipText="View supplier in Panorays"
          />
        );
      },
    });
  }

  if (AZURE_LIST_BLOB_CONTAINER_CONTROL_IDS.includes(fullControl.control_id)) {
    columns.push({
      field: "link",
      headerName: "List View",
      align: "left",
      sortable: false,
      width: 100,
      maxWidth: 100,
      disableReorder: true,
      renderCell: (params) => {
        const storageAccountName =
          params.row?.azure_blob_container_data?.storage_account_name;
        const blobContainerName =
          params.row?.azure_blob_container_data?.container_name;
        if (!storageAccountName || !blobContainerName) {
          return "-";
        }
        return (
          <ExternalLink
            href={`https://${storageAccountName}.blob.core.windows.net/${blobContainerName}?restype=container&comp=list`}
            text=""
            linkText={""}
            linkColor={colors.text_primary}
            tooltipText="List blob container"
          />
        );
      },
    });
  }

  if (AWS_S3_BUCKET_PUBLIC_READ_CONTROL_IDS.includes(fullControl.control_id)) {
    columns.push(
      {
        field: "permissions",
        headerName: "Public Permissions",
        alignHeader: "left",
        align: "left",
        sortable: true,
        width: 140,
        maxWidth: 140,
        disableReorder: true,
        renderCell: (params) => {
          const publicPermissions =
            params?.row?.extra_info?.filter(
              (permission) =>
                permission === PublicDataStorePermissions.PUBLIC_READ ||
                permission === PublicDataStorePermissions.PUBLIC_WRITE
            ) || [];

          const displayPermissions = publicPermissions.map((permission) => {
            if (permission === PublicDataStorePermissions.PUBLIC_READ)
              return "Read";
            if (permission === PublicDataStorePermissions.PUBLIC_WRITE)
              return "Write";
            return permission;
          });

          return (
            <Box
              onClick={(e) => e.stopPropagation()}
              display="flex"
              alignItems="center"
              gap={0.25}
            >
              <Typography variant="caption" color={colors.text_secondary}>
                {displayPermissions.length > 0
                  ? displayPermissions.join(", ")
                  : "-"}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        align: "right",
        disableExport: true,
        sortable: false,
        maxWidth: 170,
        width: 170,
        disableReorder: true,
        disableClickEventBubbling: true,
        resizable: false,
        renderCell: (params) => {
          const misconfiguration =
            misconfigurationByEntityId[params.row.entity_id];

          const xmlUrl = getS3BucketPublicURL(params.row);
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink
                href={xmlUrl || ""}
                text=""
                linkText={
                  <Typography variant="caption" color={colors.text_secondary}>
                    View
                  </Typography>
                }
                linkColor={colors.text_secondary}
                tooltipText="View S3 bucket XML listing"
              />
              {!misconfiguration.is_excluded && (
                <JiraInteractionManager
                  fullControl={fullControl}
                  isPerMisconfiguration={true}
                  selectedMisconfigurations={[misconfiguration]}
                  sx={{
                    height: "32px",
                    border: "0px",
                  }}
                  hideTitle={true}
                  hideTooltip={true}
                />
              )}

              {misconfiguration?.is_excluded && (
                <ExclusionInformationIcon misconfiguration={misconfiguration} />
              )}

              {misconfiguration?.is_snoozed && (
                <SnoozeInformationIcon misconfiguration={misconfiguration} />
              )}

              <MisconfigurationMoreActionsButton
                fullControl={fullControl}
                misconfiguration={misconfiguration}
                onEvidenceClick={onEvidenceClick}
              />
            </Box>
          );
        },
      }
    );
  }

  columns.push(getLabelColumn());

  if (!AWS_S3_BUCKET_PUBLIC_READ_CONTROL_IDS.includes(fullControl.control_id)) {
    columns.push({
      field: "actions",
      headerName: "",
      align: "center",
      disableExport: true,
      sortable: false,
      maxWidth: 100,
      disableReorder: true,
      disableClickEventBubbling: true,
      resizable: false,
      renderCell: (params) => {
        const misconfiguration =
          misconfigurationByEntityId[params.row.entity_id];
        return (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            onClick={(e) => e.stopPropagation()}
          >
            {misconfiguration && !misconfiguration.is_excluded && (
              <JiraInteractionManager
                fullControl={fullControl}
                isPerMisconfiguration={true}
                selectedMisconfigurations={[misconfiguration]}
                sx={{
                  height: "32px",
                  border: "0px",
                }}
                hideTitle={true}
                hideTooltip={true}
              />
            )}

            {misconfiguration?.is_excluded && (
              <ExclusionInformationIcon misconfiguration={misconfiguration} />
            )}

            {misconfiguration?.is_snoozed && (
              <SnoozeInformationIcon misconfiguration={misconfiguration} />
            )}

            <MisconfigurationMoreActionsButton
              fullControl={fullControl}
              misconfiguration={misconfiguration}
              onEvidenceClick={onEvidenceClick}
            />
          </Box>
        );
      },
    });
  }

  return columns;
}
